import { useRef, useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function WizeAIPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useSEO({
    title: 'WizeAI | AI-Powered Analytics Assistant by ProtectWize',
    description: 'Experience WizeAI, ProtectWize\'s intelligent analytics assistant. Get instant insights, automated analysis, and AI-powered recommendations for your business data.',
    keywords: 'wizeai, ai analytics, artificial intelligence, data assistant, automated insights, ai-powered analytics',
    canonical: `${import.meta.env.VITE_SITE_URL}/wizeai`,
    ogType: 'website',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'WizeAI',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'AI-powered analytics assistant providing instant insights, automated analysis, and intelligent recommendations',
      url: `${import.meta.env.VITE_SITE_URL}/wizeai`,
      provider: {
        '@type': 'Organization',
        name: 'ProtectWize'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      },
      featureList: 'AI-powered insights, Automated analysis, Natural language queries, Real-time recommendations'
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Canvas animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Particle {
      x: number; y: number; size: number; speedX: number; speedY: number;
      color: string; opacity: number; pulseSpeed: number; pulsePhase: number;
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        const colors = ['#14b8a6', '#06b6d4', '#8b5cf6', '#a855f7'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.opacity = Math.random() * 0.5 + 0.3;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }
      update() {
        this.x += this.speedX; this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
        this.pulsePhase += this.pulseSpeed;
      }
      draw() {
        if (!ctx) return;
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * pulse, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity * pulse;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    const particles: Particle[] = [];
    for (let i = 0; i < 60; i++) particles.push(new Particle());

    let animId: number;
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = '#14b8a6';
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - distance / 120) * 0.3;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        });
      });
      animId = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col relative">
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{ mixBlendMode: 'screen' }}
      />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-24 pb-8 bg-gradient-to-b from-gray-900/80 to-gray-950/80">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
              <i className="ri-sparkling-line text-teal-400"></i>
              <span className="text-sm text-teal-300">Powered by your own AI model</span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Wize<span className="text-teal-400">AI</span>
            </h1>

            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed mb-4">
              Your intelligent assistant for BigData analysis, DevOps, Cloud, Security, Crypto, Web3, and more
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-800/60 rounded-full border border-gray-700">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></span>
              <span className="text-xs text-gray-400">Model: <span className="text-teal-300 font-mono">wize-ai-3.5</span></span>
            </div>
          </div>
        </section>

        {/* Embedded WebUI */}
        <section className="flex-1 pb-0 bg-transparent">
          <div className="w-full px-0">
            <div className="w-full bg-gray-900/60 backdrop-blur-sm border-t border-gray-800 overflow-hidden"
              style={{ height: 'calc(100vh - 220px)', minHeight: '600px' }}>
              <iframe
                src="https://wizeai.protectwize.com/"
                title="WizeAI Chat Interface"
                className="w-full h-full border-0"
                allow="microphone; camera; clipboard-read; clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
              />
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
